/**
 * Cross-tab mutual exclusion for operations that must run once per origin.
 *
 * Tier 1 is the Web Locks API. It only exists in a secure context, so tier 2 is
 * a TTL lease in localStorage confirmed by re-reading the key. When neither can
 * be acquired the caller is told so and MUST NOT run anyway: the previous
 * implementation fell through to an unlocked call, which let several tabs spend
 * the same refresh token and get the whole token family invalidated.
 */

import { safeJsonParse } from './safeJson';

const LEASE_PREFIX = 'tempedge:lock:';
const LEASE_TTL_MS = 15000;
const LEASE_POLL_MS = 200;
const DEFAULT_TIMEOUT_MS = 20000;

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const heldLeases = new Set();

const leaseKey = (name) => `${LEASE_PREFIX}${name}`;

const readLease = (name) => safeJsonParse(localStorage.getItem(leaseKey(name)));

/**
 * Best-effort compare-and-swap. Two tabs may write concurrently, so the claim is
 * confirmed by reading the key back; the tab that does not see its own id loses.
 * The TTL bounds how long a crashed holder can block the others.
 */
const tryAcquireLease = (name) => {
  const current = readLease(name);
  const now = Date.now();

  if (current && current.id !== tabId && current.expiresAt > now) return false;

  try {
    localStorage.setItem(leaseKey(name), JSON.stringify({ id: tabId, expiresAt: now + LEASE_TTL_MS }));
  } catch (error) {
    return false;
  }

  return readLease(name)?.id === tabId;
};

const releaseLease = (name) => {
  if (readLease(name)?.id === tabId) localStorage.removeItem(leaseKey(name));
  heldLeases.delete(name);
};

const hasWebLocks = () => typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';

const delay = (durationMs) => new Promise((resolve) => setTimeout(resolve, durationMs));

const runUnderLease = async (name, task, timeoutMs) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (tryAcquireLease(name)) {
      heldLeases.add(name);
      const renew = setInterval(() => tryAcquireLease(name), LEASE_TTL_MS / 3);
      try {
        return { acquired: true, value: await task() };
      } finally {
        clearInterval(renew);
        releaseLease(name);
      }
    }
    await delay(LEASE_POLL_MS);
  }

  return { acquired: false, value: undefined };
};

const runUnderWebLock = async (name, task, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const value = await navigator.locks.request(name, { mode: 'exclusive', signal: controller.signal }, () => task());
    return { acquired: true, value };
  } catch (error) {
    if (error?.name === 'AbortError') return { acquired: false, value: undefined };
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Runs `task` while holding an exclusive origin-wide lock.
 *
 * Resolves to `{ acquired, value }`. `acquired: false` means the lock could not
 * be taken within `timeoutMs` and `task` never ran. A rejection from `task` is
 * propagated unchanged and is never retried outside the lock.
 */
export const withCrossTabLock = (name, task, { timeoutMs = DEFAULT_TIMEOUT_MS } = {}) =>
  hasWebLocks() ? runUnderWebLock(name, task, timeoutMs) : runUnderLease(name, task, timeoutMs);

/** Drops every lease this tab still holds, so a closing tab never blocks the others. */
const releaseAllLeases = () => {
  Array.from(heldLeases).forEach(releaseLease);
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', releaseAllLeases);
}
